export default {
  slug: "24-hours-in-paris-what-to-delegate",
  date: "2026-10-01",
  en: {
    category: "Guides",
    readTime: "5 min",
    title: "24 Hours in Paris: What Should You Delegate?",
    abstract: "With one day in the city, time is the only currency. A practical list of what to hand to someone else, hour by hour — and what to keep for yourself.",
    seoTitle: "24 Hours in Paris: What to Delegate | Book & Smile",
    seoDesc: "One day in Paris, planned around time rather than sights: what to delegate to a concierge the same day — car, table, gift, appointment, errand — and what to keep for yourself.",
    answer:
      "With 24 hours in Paris, delegate everything that is a phone call, a queue or a journey: the car from the airport, the table that is 'complet' online, the gift that must arrive before the dinner, the appointment that needs a call in French, the errand across town. Keep the walking, the eating and the looking. The day is short; the logistics should not be part of it.",
    sections: [
      {
        h2: "Morning: arrive without thinking",
        paras: ["The first hours decide the day."],
        list: [
          "Delegate: the chauffeur at CDG, Orly, Le Bourget or Gare du Nord — flight or train followed, luggage sent to the hotel while you go to the first appointment.",
          "Delegate: early check-in or a room to change in, requested the day before rather than hoped for at the desk.",
          "Keep: the first coffee, standing, at a counter in the neighbourhood where you will spend the day.",
        ],
      },
      {
        h2: "Midday: the appointments that need French",
        paras: ["Paris still works by telephone, in French, during Paris hours — which is precisely when you are in meetings."],
        list: [
          "Delegate: the private shopping appointment at the maison, the tailor, the optician, the doctor who speaks English this afternoon.",
          "Delegate: the sworn translator, the document that must reach an administration today, the courier across the river.",
          "Keep: lunch — short, good, nearby, booked for you so you do not spend it searching.",
        ],
      },
      {
        h2: "Evening: the table and everything around it",
        paras: [
          "The dinner is usually the point of the day, and the thing most often left to chance. Hand it over early: the restaurant that is fully booked online is often available to the person who calls at 17:45; the gift for your host can be chosen, wrapped and delivered to the table; the car can wait outside rather than be summoned from an app in the rain. Our guide to last-minute restaurant reservations explains how the timing works.",
        ],
      },
      {
        h2: "What to keep",
        paras: [
          "Keep the walk along the river at the end, the half hour in a church or a garden, the bookshop, the unplanned glass of wine. These are what you came for, and no one can delegate them for you. Everything else — the calls, the queues, the journeys — is what a last-minute concierge in Paris exists to remove from a short day.",
        ],
      },
    ],
    faqs: [
      { q: "Can all of this be arranged on the day itself?", a: "Most of it, yes — car, table, gift, courier, doctor — within the limits of availability. Private museum visits and the rarest tables need notice; we say so immediately." },
      { q: "Do I need to be a member for one day?", a: "No. One-off assistance for a single day is common; members simply get faster handling because preferences are on file." },
      { q: "How do I send a request?", a: "A WhatsApp message with what you need and by when. We confirm what is realistic within minutes." },
      { q: "What if I am only in Paris between two flights?", a: "A layover day is the most common 24-hour request. We arrange the car from and back to the airport, luggage held, and the hours in between." },
    ],
    ctaLabel: "See the Last-Minute Concierge in Paris",
    ctaTo: "/last-minute-concierge-paris",
  },
  fr: {
    category: "Guides",
    readTime: "5 min",
    title: "24 heures à Paris : que déléguer ?",
    abstract: "Avec une journée en ville, le temps est la seule monnaie. Une liste pratique de ce qu'il faut confier à quelqu'un d'autre, heure par heure — et de ce qu'il faut garder pour soi.",
    seoTitle: "24 heures à Paris : que déléguer ? | Book & Smile",
    seoDesc: "Une journée à Paris planifiée autour du temps plutôt que des sites : que déléguer à une conciergerie le jour même — voiture, table, cadeau, rendez-vous, course — et que garder pour soi.",
    answer:
      "Avec 24 heures à Paris, déléguez tout ce qui est un appel, une file ou un trajet : la voiture depuis l'aéroport, la table « complète » en ligne, le cadeau qui doit arriver avant le dîner, le rendez-vous qui exige un appel en français, la course à l'autre bout de la ville. Gardez la marche, la table et le regard. La journée est courte ; la logistique ne devrait pas en faire partie.",
    sections: [
      {
        h2: "Le matin : arriver sans y penser",
        paras: ["Les premières heures décident de la journée."],
        list: [
          "Déléguer : le chauffeur à CDG, Orly, Le Bourget ou gare du Nord — vol ou train suivi, bagages envoyés à l'hôtel pendant que vous allez au premier rendez-vous.",
          "Déléguer : l'arrivée anticipée ou une chambre pour se changer, demandée la veille plutôt qu'espérée à la réception.",
          "Garder : le premier café, debout, au comptoir, dans le quartier où vous passerez la journée.",
        ],
      },
      {
        h2: "Midi : les rendez-vous qui exigent du français",
        paras: ["Paris fonctionne encore par téléphone, en français, aux heures de Paris — précisément quand vous êtes en réunion."],
        list: [
          "Déléguer : le rendez-vous de shopping privé chez la maison, le tailleur, l'opticien, le médecin anglophone cet après-midi.",
          "Déléguer : le traducteur assermenté, le document qui doit atteindre une administration aujourd'hui, le coursier de l'autre côté de la Seine.",
          "Garder : le déjeuner — court, bon, proche, réservé pour vous afin de ne pas le passer à chercher.",
        ],
      },
      {
        h2: "Le soir : la table et tout ce qui l'entoure",
        paras: [
          "Le dîner est généralement le but de la journée, et ce que l'on laisse le plus souvent au hasard. Confiez-le tôt : le restaurant complet en ligne est souvent disponible pour qui appelle à 17 h 45 ; le cadeau pour votre hôte peut être choisi, emballé et livré à table ; la voiture peut attendre dehors plutôt que d'être appelée depuis une appli sous la pluie. Notre guide des réservations de dernière minute explique le timing.",
        ],
      },
      {
        h2: "Ce qu'il faut garder",
        paras: [
          "Gardez la marche le long du fleuve à la fin, la demi-heure dans une église ou un jardin, la librairie, le verre de vin imprévu. C'est pour cela que vous êtes venu, et personne ne peut le déléguer pour vous. Tout le reste — les appels, les files, les trajets — est ce qu'une conciergerie de dernière minute à Paris existe pour retirer d'une courte journée.",
        ],
      },
    ],
    faqs: [
      { q: "Tout cela peut-il s'organiser le jour même ?", a: "L'essentiel, oui — voiture, table, cadeau, coursier, médecin — dans les limites des disponibilités. Les visites privées de musées et les tables les plus rares demandent du délai ; nous le disons immédiatement." },
      { q: "Faut-il être membre pour une seule journée ?", a: "Non. L'assistance ponctuelle pour une journée est courante ; les membres bénéficient simplement d'un traitement plus rapide car leurs préférences sont au dossier." },
      { q: "Comment envoyer une demande ?", a: "Un message WhatsApp avec ce dont vous avez besoin et pour quand. Nous confirmons ce qui est réaliste en quelques minutes." },
      { q: "Et si je ne suis à Paris qu'entre deux vols ?", a: "L'escale est la demande de 24 heures la plus courante. Nous organisons la voiture depuis et vers l'aéroport, les bagages gardés, et les heures entre les deux." },
    ],
    ctaLabel: "Voir la conciergerie de dernière minute à Paris",
    ctaTo: "/last-minute-concierge-paris",
  },
};
